/**
 * Home screen — the entry point Eva sees once authenticated.
 *
 * Three layout variants chosen from the result of `GET /api/weeks`:
 *
 *   1. First launch (no current, no archived) — Pumpkin says hi, copy
 *      directs Eva to ask a grown-up, single big "Set up Eva's spelling
 *      words" CTA that bypasses the long-press and opens the password
 *      prompt directly (spec §3.1, §5.6).
 *
 *   2. No-current-but-past-exists — "No new words yet — practise old
 *      ones?" reframe per spec §5.5. Big CTA goes to past-weeks; My
 *      Stickers is still visible; Play Today is hidden.
 *
 *   3. Normal — the three spec §3.1 actions: Play Today (focal), Past
 *      Weeks, My Stickers.
 *
 * Long-press on Pumpkin (~2s) opens the parent password prompt regardless
 * of variant. The hold has a subtle visual response so the parent knows
 * the press is being recognised (spec §7).
 */
import { useCallback, useEffect, useState } from "react";

import { Button } from "@/components/Button";
import { Modal } from "@/components/Modal";
import { PasswordPrompt } from "@/components/PasswordPrompt";
import { PumpkinFigure } from "@/components/PumpkinFigure";
import { WhatsNewModal } from "@/components/WhatsNewModal";
import { useNavigation } from "@/context/NavigationContext";
import { useSession } from "@/context/SessionContext";
import { useLongPress } from "@/hooks/useLongPress";
import { api } from "@/lib/api";
import {
  latestUnseenAnnouncement,
  LATEST_ANNOUNCEMENT_VERSION,
  type Announcement,
} from "@/lib/announcements";
import "./HomeScreen.css";

type WeeksState =
  | { kind: "loading" }
  | { kind: "first-launch" }
  | { kind: "no-current"; hasPast: boolean }
  | { kind: "normal" }
  | { kind: "error"; message: string };

export function HomeScreen() {
  const { navigate } = useNavigation();
  const { session, resumePhase, clearSession } = useSession();
  const [state, setState] = useState<WeeksState>({ kind: "loading" });
  const [parentPromptOpen, setParentPromptOpen] = useState(false);
  const [holdActive, setHoldActive] = useState(false);
  const [resumePromptOpen, setResumePromptOpen] = useState(false);
  const [whatsNew, setWhatsNew] = useState<Announcement | null>(null);
  const [currentStreak, setCurrentStreak] = useState(0);

  // True iff SessionProvider rehydrated a recent *current-week*
  // in-progress session. Past-week reviews live outside the
  // "Play Today" mental model — if there's a past-week snapshot
  // hanging around, Play Today should silently discard it and start
  // fresh rather than offer to resume into a past-week flow.
  const canResume =
    session !== null && resumePhase !== null && session.countsTowardProgress;

  // "Play Today" — if an in-flight session exists (rehydrated from
  // localStorage within the 10-min window) we ask Eva before clobbering
  // it. Otherwise the fresh-start path runs (clear + navigate).
  const handlePlayToday = useCallback(() => {
    if (canResume) {
      setResumePromptOpen(true);
      return;
    }
    clearSession();
    navigate("flashCard");
  }, [canResume, clearSession, navigate]);

  const handleResumeYes = useCallback(() => {
    if (!resumePhase) return;
    setResumePromptOpen(false);
    navigate(resumePhase);
  }, [resumePhase, navigate]);

  const handleResumeStartOver = useCallback(() => {
    setResumePromptOpen(false);
    clearSession();
    navigate("flashCard");
  }, [clearSession, navigate]);

  const handleReviewPastWeeks = useCallback(() => {
    // Past-week reviews always start a brand-new session — they're
    // their own kind of session and trying to resume one into the
    // current-week flow would be confusing.
    clearSession();
    navigate("pastWeeks");
  }, [clearSession, navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [weeksRes, settingsRes, streakRes] = await Promise.all([
        api.listWeeks(),
        api.getSettings(),
        api.getStreak(),
      ]);
      if (cancelled) return;
      if (streakRes.status === 200 && streakRes.body) {
        setCurrentStreak(streakRes.body.currentStreak);
      }
      const { status, body } = weeksRes;
      if (status === 401) {
        // Session expired between login and home render — extremely unlikely
        // but technically possible. The AuthProvider will pick it up on the
        // next /api/session refresh; until then, show a polite error.
        setState({ kind: "error", message: "Please reload the page." });
        return;
      }
      if (status !== 200 || !body) {
        setState({ kind: "error", message: "Something's not working right now." });
        return;
      }
      const firstLaunch = body.current === null && body.archived.length === 0;
      if (firstLaunch) {
        setState({ kind: "first-launch" });
      } else if (body.current === null) {
        setState({ kind: "no-current", hasPast: body.archived.length > 0 });
      } else {
        setState({ kind: "normal" });
      }
      // "What's New" — only for an established install (never on the very
      // first launch, where there's nothing yet to contrast against).
      if (!firstLaunch && settingsRes.status === 200 && settingsRes.body) {
        const unseen = latestUnseenAnnouncement(settingsRes.body.lastAnnouncementSeen);
        if (unseen) setWhatsNew(unseen);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const dismissWhatsNew = useCallback(() => {
    setWhatsNew(null);
    // Fire-and-forget — Eva doesn't wait on the network. Persists the
    // highest version so the modal won't show again until a future bump.
    void api.updateSettings({ lastAnnouncementSeen: LATEST_ANNOUNCEMENT_VERSION });
  }, []);

  const longPressHandlers = useLongPress(
    () => {
      setHoldActive(false);
      setParentPromptOpen(true);
    },
    {
      onHoldStart: () => setHoldActive(true),
      onHoldEnd: () => setHoldActive(false),
    },
  );

  const handleParentSuccess = useCallback(() => {
    setParentPromptOpen(false);
    navigate("parent");
  }, [navigate]);

  return (
    <main className="home">
      <div className="home__inner">
        <button
          type="button"
          className={`home__pumpkin-press ${holdActive ? "is-holding" : ""}`}
          aria-label="Pumpkin (long-press for parent area)"
          onContextMenu={(e) => e.preventDefault()}
          {...longPressHandlers}
        >
          <PumpkinFigure
            className="home__pumpkin pumpkin-idle"
            pose="idleDefault"
            alt="Pumpkin the pangolin, waiting to play"
          />
        </button>

        {state.kind === "loading" && <p className="home__subtitle">…</p>}

        {state.kind === "error" && <p className="home__error">{state.message}</p>}

        {state.kind === "first-launch" && (
          <>
            <h1 className="home__title">Eva&rsquo;s Spelling Game</h1>
            <p className="home__subtitle">
              Ask Mum or Dad to set up this week&rsquo;s spelling words.
            </p>
            <div className="home__actions">
              <Button size="large" onClick={() => setParentPromptOpen(true)}>
                Set up Eva&rsquo;s spelling words
              </Button>
            </div>
          </>
        )}

        {state.kind === "no-current" && (
          <>
            <h1 className="home__title">Hi Eva!</h1>
            <p className="home__subtitle">
              No new words yet this week.
              {state.hasPast && " Want to practise an old set?"}
            </p>
            <div className="home__actions">
              {state.hasPast && (
                <Button size="large" onClick={handleReviewPastWeeks}>
                  Practise past words
                </Button>
              )}
              <Button
                size="small"
                variant="secondary"
                onClick={() => navigate("myStickers")}
              >
                My Stickers
              </Button>
              <Button
                size="small"
                variant="secondary"
                onClick={() => navigate("alphabet")}
              >
                ABC
              </Button>
            </div>
          </>
        )}

        {state.kind === "normal" && (
          <>
            <h1 className="home__title">Hi Eva!</h1>
            <div className="home__actions">
              <Button size="large" onClick={handlePlayToday}>
                Play Today
              </Button>
              <div className="home__secondary-row">
                <Button
                  size="small"
                  variant="secondary"
                  onClick={handleReviewPastWeeks}
                >
                  Past Weeks
                </Button>
                <Button
                  size="small"
                  variant="secondary"
                  onClick={() => navigate("myStickers")}
                >
                  My Stickers
                </Button>
                <Button
                  size="small"
                  variant="secondary"
                  onClick={() => navigate("alphabet")}
                >
                  ABC
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      <WhatsNewModal
        announcement={whatsNew}
        currentStreak={currentStreak}
        onDismiss={dismissWhatsNew}
      />

      <PasswordPrompt
        open={parentPromptOpen}
        title="Parent area"
        description="Enter the password to set up words and settings."
        submitLabel="Enter"
        onSuccess={handleParentSuccess}
        onClose={() => setParentPromptOpen(false)}
      />

      <Modal
        open={resumePromptOpen}
        onClose={() => setResumePromptOpen(false)}
        titleId="resume-title"
      >
        <h2 id="resume-title" className="home__modal-title">
          Keep going where you left off?
        </h2>
        <p className="home__modal-body">
          You started some spelling earlier. Want to carry on, or start fresh?
        </p>
        <div className="home__modal-actions">
          <Button variant="secondary" onClick={handleResumeStartOver}>
            Start fresh
          </Button>
          <Button onClick={handleResumeYes}>Keep going</Button>
        </div>
      </Modal>
    </main>
  );
}
